import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import {
  NoAccountModalProvider,
  useFetchQuery,
  useSegment,
} from '@sparknz/journeys-common';

import { PlansAccountListQuery } from '__generated__/PlansAccountListQuery.graphql';
import { PlansCategoriesQuery } from '__generated__/PlansCategoriesQuery.graphql';
import MobilePlanCards from '../../../MobilePlans/components/MobilePlanCards';
import { useCategorySelection } from '../../providers';
import { ConfigurablePlanCards } from '../../typings/plans';
import { SubCategory as Subcategory } from '../../typings/product';
import SubcategorySelector from '../SubCategorySelector';

type PlansProps = ConfigurablePlanCards;

const ROLLOVER_PLAN_SUBCATEGORY_ID = 'rollover_plan';

const plansSubcategoriesQuery = graphql`
  query PlansCategoriesQuery($categoryId: String!, $channel: String!) {
    eligibleProductOffersByCategory(
      categoryId: $categoryId
      channel: $channel
    ) {
      code
      message
      category {
        isCategoryContainer
        subCategories {
          id
          name
          visibility
          defaultSelected
        }
      }
    }
  }
`;

const accountListQuery = graphql`
  query PlansAccountListQuery {
    me {
      accountList {
        accountId
        segment
      }
    }
  }
`;

function excludeSubcategoriesForChangePlan(subcategories: Subcategory[]) {
  const changePlanExcludedCategories = ['wearable_plan', 'data_plan'];
  return subcategories?.filter(
    (subCategory) => !changePlanExcludedCategories.includes(subCategory.id),
  );
}

export default function Plans(props: PlansProps) {
  const { querySubCategoryId, selectedCategoryId } = useCategorySelection();
  const { channel, segment, isBusiness } = useSegment();
  const environment = useRelayEnvironment();

  const { fetch: fetchQuery } = useFetchQuery<PlansAccountListQuery>(
    accountListQuery,
    environment,
  );

  const fetchAccountList = async () => {
    const fetchAccountListResponse = await fetchQuery({});
    return fetchAccountListResponse.me.accountList;
  };

  const accountListResponse = useLazyLoadQuery<PlansAccountListQuery>(
    accountListQuery,
    {},
  );

  const subcategoriesResponse = useLazyLoadQuery<PlansCategoriesQuery>(
    plansSubcategoriesQuery,
    {
      channel,
      categoryId:
        selectedCategoryId === 'change_plan' ? 'postpaid' : selectedCategoryId!,
    },
  );

  const selectedSubcategoriesData =
    subcategoriesResponse.eligibleProductOffersByCategory;

  const visibleSubcategories =
    selectedSubcategoriesData?.category?.subCategories?.filter(
      (subCategory) => subCategory.visibility,
    ) || [];

  const subcategorySelectorCategories =
    selectedCategoryId === 'change_plan'
      ? excludeSubcategoriesForChangePlan(visibleSubcategories)
      : visibleSubcategories;

  const isSubcategoryValid = visibleSubcategories?.some(
    ({ id }) => id === querySubCategoryId,
  );

  const getSelectedCategoryId = () =>
    (isBusiness
      ? visibleSubcategories?.find(
          (subCategory) => subCategory.id === ROLLOVER_PLAN_SUBCATEGORY_ID,
        )?.id
      : null) || visibleSubcategories[0]?.id;

  const selectedSubcategoryId = isSubcategoryValid
    ? querySubCategoryId!
    : getSelectedCategoryId();

  return (
    <NoAccountModalProvider
      accountList={accountListResponse.me.accountList}
      fetchAccountList={fetchAccountList}
      segment={segment}
    >
      <SubcategorySelector
        subCategories={subcategorySelectorCategories}
        selectedSubCategoryId={selectedSubcategoryId}
      />
      <MobilePlanCards
        {...props}
        selectedCategoryId={selectedCategoryId!}
        selectedSubCategoryId={selectedSubcategoryId}
      />
    </NoAccountModalProvider>
  );
}
